import { useEffect, useRef, useState } from "react";
import {
  listPrograms,
  listProgramsByCoachAdmin,
  listSessions,
  listEnrollments,
  listAllEnrollments,
  createEnrollment,
  startEnrollmentCheckout,
  listCoaches,
  assignCoach,
  updateEnrollment,
  deleteEnrollment,
} from "../../../apis/programsApi";
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { formatDateForProgram } from "../../../utils/DateUtils";
import { AddEnrollmentDialog } from "../../../components/admin/programs/AddEnrollmentDialog";
import { EditEnrollmentDialog } from "../../../components/admin/programs/EditEnrollmentDialog";
import { ViewEnrollmentDialog } from "../../../components/admin/programs/ViewEnrollmentDialog";
import { EnrollmentsDataGrid } from "../../../components/admin/programs/EnrollmentsDataGrid";
import { subscribeProgramEnrollmentConfirmed } from "../../../utils/programEnrollmentEvents";

const ManageProgramEnrollments = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [coaches, setCoaches] = useState<any[]>([]);
  const [editingCoach, setEditingCoach] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingCurrent, setDownloadingCurrent] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [statusTab, setStatusTab] = useState(0);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<any | null>(null);
  const enrollmentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeSessionIdRef = useRef("");
  const loadGenerationRef = useRef(0);
  
  // Get user type for role-based restrictions
  const userType = localStorage.getItem("userType");
  const canEdit = ["admin", "employee", "employee_admin", "coach_admin"].includes(userType || "");

  const activePrograms = programs.filter((p) => Boolean(p.isActive));
  const inactivePrograms = programs.filter((p) => !p.isActive);
  const displayedPrograms = statusTab === 0 ? activePrograms : inactivePrograms;
  const selectedProgramInTab = displayedPrograms.some((p) => p.id === selectedProgramId);

  const getEnrollmentSessionId = (enrollment: any) =>
    enrollment?.session?.id || enrollment?.sessionId || "";

  const applyEnrollmentsIfCurrent = (sessionId: string, generation: number, data: any[]) => {
    if (loadGenerationRef.current !== generation) return;
    if (activeSessionIdRef.current !== sessionId) return;
    const filtered = data.filter((enrollment) => getEnrollmentSessionId(enrollment) === sessionId);
    setEnrollments(filtered);
  };

  const loadEnrollmentsForSession = async (
    sessionId: string,
    options: { generation?: number } = {}
  ) => {
    if (!sessionId) return [];
    const generation = options.generation ?? ++loadGenerationRef.current;

    const fromDb = await listEnrollments(sessionId);
    applyEnrollmentsIfCurrent(sessionId, generation, fromDb);
    return fromDb;
  };

  useEffect(() => {
    (async () => {
      let p
      if (['admin', 'employee', 'employee_admin'].includes(userType || "")) {
        p = await listPrograms();
      }
      else {
        p = await listProgramsByCoachAdmin(localStorage.getItem("userId") || "");
      }
      setPrograms(p);
      const firstActive = p.find((prog: any) => Boolean(prog.isActive));
      setSelectedProgramId(firstActive?.id || "");
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const c = await listCoaches();
        setCoaches(c);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

useEffect(() => {
  activeSessionIdRef.current = selectedSessionId;
}, [selectedSessionId]);

useEffect(() => {
  (async () => {
    // When program changes, immediately clear sessions, selected session, and enrollments
    setSessions([]);
    setSelectedSessionId("");
    setEnrollments([]);
    loadGenerationRef.current += 1;

    if (!selectedProgramId) return;
    const s = await listSessions(selectedProgramId);
    setSessions(s);
    setSelectedSessionId(s.length > 0 ? (s[0].id || s[0].sessionId) : "");
  })();
}, [selectedProgramId]);

useEffect(() => {
  if (!selectedSessionId) {
    setEnrollments([]);
    return;
  }

  setEnrollments([]);
  const generation = ++loadGenerationRef.current;
  loadEnrollmentsForSession(selectedSessionId, { generation });
}, [selectedSessionId]);

  const refreshEnrollmentsFromDb = async (sessionId: string) => {
    const generation = ++loadGenerationRef.current;
    return loadEnrollmentsForSession(sessionId, { generation });
  };

  useEffect(() => {
    const refreshEnrollments = async () => {
      if (!selectedSessionId) return;
      await loadEnrollmentsForSession(selectedSessionId);
    };

    const handleFocus = () => {
      refreshEnrollments();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [selectedSessionId]);

  useEffect(() => {
    const refreshEnrollments = async (programSessionId?: string) => {
      if (!selectedSessionId) return;
      if (programSessionId && programSessionId !== selectedSessionId) return;
      if (enrollmentPollRef.current) {
        clearInterval(enrollmentPollRef.current);
        enrollmentPollRef.current = null;
      }
      await loadEnrollmentsForSession(selectedSessionId);
      setSnackbar({
        open: true,
        message: "Enrollment confirmed and added to the list.",
        severity: "success",
      });
    };

    return subscribeProgramEnrollmentConfirmed((programSessionId) => {
      refreshEnrollments(programSessionId);
    });
  }, [selectedSessionId]);

  useEffect(() => {
    return () => {
      if (enrollmentPollRef.current) {
        clearInterval(enrollmentPollRef.current);
      }
    };
  }, []);

  const startEnrollmentPolling = (sessionId: string, initialCount: number) => {
    if (enrollmentPollRef.current) {
      clearInterval(enrollmentPollRef.current);
    }

    const poll = async () => {
      if (activeSessionIdRef.current !== sessionId) return;
      try {
        const latest = await loadEnrollmentsForSession(sessionId);
        if (latest.length > initialCount) {
          if (enrollmentPollRef.current) {
            clearInterval(enrollmentPollRef.current);
            enrollmentPollRef.current = null;
          }
          setSnackbar({
            open: true,
            message: "Enrollment confirmed and added to the list.",
            severity: "success",
          });
        }
      } catch {
        // keep polling until payment completes
      }
    };

    poll();
    enrollmentPollRef.current = setInterval(poll, 1000);

    setTimeout(() => {
      if (enrollmentPollRef.current) {
        clearInterval(enrollmentPollRef.current);
        enrollmentPollRef.current = null;
      }
    }, 15 * 60 * 1000);
  };

  const handleCreate = async (withCheckout: boolean, formData: any, checkoutWindow?: Window | null) => {
    if (!selectedSessionId) return;
    try {
      if (withCheckout) {
        const url = await startEnrollmentCheckout(selectedSessionId, formData);
        if (!url) {
          throw new Error("Checkout URL was not returned");
        }
        if (checkoutWindow) {
          checkoutWindow.location.href = url;
        } else {
          window.location.href = url;
        }
        startEnrollmentPolling(selectedSessionId, enrollments.length);
        setSnackbar({
          open: true,
          message: "Checkout opened. The enrollment will appear here automatically after payment is confirmed.",
          severity: "success",
        });
      } else {
        // Create enrollment directly with pending status
        await createEnrollment(selectedSessionId, formData);
        await loadEnrollmentsForSession(selectedSessionId);
        setSnackbar({ 
          open: true, 
          message: "Enrollment created. Payment is set to pay at counter.", 
          severity: "success" 
        });
      }
    } catch (error: any) {
      checkoutWindow?.close();
      setSnackbar({ 
        open: true, 
        message: withCheckout ? error.response?.data?.message || "Failed to start checkout" : "Failed to create enrollment", 
        severity: "error" 
      });
      throw error;
    }
  };

  const onCoachChange = (enrollmentId: string, coachId: string) => {
    setEditingCoach({ ...editingCoach, [enrollmentId]: coachId });
  };

  const onCoachSave = async (enrollmentId: string) => {
    const coachId = editingCoach[enrollmentId];
    if (coachId === undefined) return;
    try {
      await assignCoach(enrollmentId, coachId);
      await loadEnrollmentsForSession(selectedSessionId);
      setSnackbar({ open: true, message: "Coach assigned successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to assign coach", severity: "error" });
    }
  };

  const handleStatusTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setStatusTab(newValue);
    const list = newValue === 0
      ? programs.filter((p) => Boolean(p.isActive))
      : programs.filter((p) => !p.isActive);
    setSelectedProgramId(list[0]?.id || "");
  };

  const handleRefresh = async () => {
    if (!selectedSessionId) return;
    setRefreshing(true);
    try {
      await loadEnrollmentsForSession(selectedSessionId);
      setSnackbar({ open: true, message: "Enrollments refreshed successfully!", severity: "success" });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to refresh enrollments", severity: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewDetails = (enrollment: any) => {
    setSelectedEnrollment(enrollment);
    setViewDialogOpen(true);
  };

  const handleEdit = (enrollment: any) => {
    setSelectedEnrollment(enrollment);
    setEditDialogOpen(true);
  };

  const handleUpdateEnrollment = async (enrollmentId: string, data: any) => {
    try {
      await updateEnrollment(enrollmentId, data);
      await loadEnrollmentsForSession(selectedSessionId);
      setSnackbar({ open: true, message: "Enrollment updated successfully!", severity: "success" });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to update enrollment", severity: "error" });
    }
  };

  const handleDeleteEnrollment = async (enrollmentId: string) => {
    try {
      setEnrollments((prev) => prev.filter((enrollment) => enrollment.id !== enrollmentId));
      await deleteEnrollment(enrollmentId);
      await refreshEnrollmentsFromDb(selectedSessionId);
      setSnackbar({ open: true, message: "Enrollment deleted successfully!", severity: "success" });
    } catch (error: any) {
      await refreshEnrollmentsFromDb(selectedSessionId);
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to delete enrollment", severity: "error" });
    }
  };

  const selectedSession = sessions.find((s) => s.id === selectedSessionId);

  const getCoachNameById = (coachId: string | undefined | null) => {
    if (!coachId) return "";
    const coach = coaches.find((c) => c.id === coachId);
    if (!coach) return "";
    const fullName = `${coach.firstName || ""} ${coach.lastName || ""}`.trim();
    return fullName || coach.email || "";
  };

  const formatAgeGroupForCsv = (participantAge: unknown) => {
    if (participantAge === null || participantAge === undefined) return "";
    const str = String(participantAge).trim();
    if (!str) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return "";
    return str;
  };

  const mapEnrollmentToCsvRow = (enrollment: any) => {
    const programTitle = enrollment.session?.program?.title || "";
    const sessionName = enrollment.session?.name || "";
    const programCoach =
      enrollment.session?.program?.programCoach === null
        ? "Unassigned"
        : `${enrollment.session?.program?.programCoach?.firstName || ""} ${enrollment.session?.program?.programCoach?.lastName || ""}`.trim() ||
          enrollment.session?.program?.programCoach?.email ||
          "";
    const sessionCoach =
      enrollment.session?.sessionCoachOrEmployee === null
        ? "Unassigned"
        : `${enrollment.session?.sessionCoachOrEmployee?.firstName || ""} ${enrollment.session?.sessionCoachOrEmployee?.lastName || ""}`.trim() ||
          enrollment.session?.sessionCoachOrEmployee?.email ||
          "";
    const enrollmentCoachId = Array.isArray(enrollment.coachIds) && enrollment.coachIds.length > 0 ? enrollment.coachIds[0] : "";
    const enrollmentCoach = enrollmentCoachId ? getCoachNameById(enrollmentCoachId) || "Unassigned" : "Unassigned";

    const startDate = enrollment.selectedStartDate
      ? formatDateForProgram(new Date(enrollment.selectedStartDate).toISOString())
      : "";
    const endDate = enrollment.selectedEndDate
      ? formatDateForProgram(new Date(enrollment.selectedEndDate).toISOString())
      : "";

    const totalAmount = enrollment.totalAmount ? Number(enrollment.totalAmount).toFixed(2) : "";

    return {
      Program: programTitle,
      Session: sessionName,
      ParticipantName: enrollment.participantName || "",
      ParticipantEmail: enrollment.user?.email || "",
      ParticipantPhone: enrollment.user?.phoneNumber || "",
      AgeGroup: formatAgeGroupForCsv(enrollment.participantAge),
      AgeYears: enrollment.participantActualAge ?? "",
      Address: enrollment.address || "",
      GuardianName: enrollment.parentGuardianName || "",
      GuardianContact: enrollment.parentGuardianContact || "",
      EmergencyContact: enrollment.emergencyContact || "",
      StartDate: startDate,
      EndDate: endDate,
      TimeSlot: enrollment.timeSlot || "",
      PaymentStatus: enrollment.paymentStatus || "",
      Amount: totalAmount,
      TaxPercent: enrollment.tax ?? "",
      ProgramCoach: programCoach,
      SessionCoach: sessionCoach,
      EnrollmentCoach: enrollmentCoach,
      MedicalConditions: enrollment.medicalConditions || "",
    };
  };

  const convertToCsv = (rows: Record<string, any>[]) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (value: any, column?: string) => {
      if (value === null || value === undefined) return "";
      let str = String(value);
      if (column === "AgeGroup" && str) {
        // Prevent Excel from auto-formatting age ranges (e.g. "9-11") as dates.
        str = `\t${str}`;
      }
      if (str.includes('"') || str.includes(",") || str.includes("\n") || str.startsWith("\t")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const headerLine = headers.map((h) => escape(h)).join(",");
    const dataLines = rows.map((row) => headers.map((h) => escape(row[h], h)).join(","));
    return [headerLine, ...dataLines].join("\n");
  };

  const downloadCsvFile = (csvContent: string, filename: string) => {
    const blob = new Blob(["\uFEFF", csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCurrentSessionCsv = async () => {
    if (!enrollments.length) {
      setSnackbar({ open: true, message: "No enrollments to export for the selected session.", severity: "error" });
      return;
    }
    try {
      setDownloadingCurrent(true);
      const rows = enrollments.map(mapEnrollmentToCsvRow);
      const csv = convertToCsv(rows);
      const programName = sessions.find((s) => s.id === selectedSessionId)?.program?.title || programs.find((p) => p.id === selectedProgramId)?.title || "Program";
      const sessionName = sessions.find((s) => s.id === selectedSessionId)?.name || "Session";
      const safeProgram = programName.replace(/[^a-z0-9]+/gi, "_");
      const safeSession = sessionName.replace(/[^a-z0-9]+/gi, "_");
      downloadCsvFile(csv, `${safeProgram}_${safeSession}_enrollments.csv`);
      setSnackbar({ open: true, message: "CSV downloaded for current session.", severity: "success" });
    } catch (error: any) {
      console.error(error);
      setSnackbar({ open: true, message: "Failed to generate CSV for current session.", severity: "error" });
    } finally {
      setDownloadingCurrent(false);
    }
  };

  const handleDownloadAllCsv = async () => {
    try {
      setDownloadingAll(true);
      const allEnrollments = await listAllEnrollments();
      const tabIsActive = statusTab === 0;
      const filteredEnrollments = allEnrollments.filter((enrollment: any) => {
        const programIsActive = Boolean(enrollment.session?.program?.isActive);
        return tabIsActive ? programIsActive : !programIsActive;
      });
      if (!filteredEnrollments.length) {
        setSnackbar({
          open: true,
          message: tabIsActive
            ? "No enrollments found to export for active programs."
            : "No enrollments found to export for inactive programs.",
          severity: "error",
        });
        return;
      }
      const rows = filteredEnrollments.map(mapEnrollmentToCsvRow);
      const csv = convertToCsv(rows);
      const statusLabel = tabIsActive ? "active" : "inactive";
      downloadCsvFile(csv, `${statusLabel}_program_enrollments.csv`);
      setSnackbar({
        open: true,
        message: tabIsActive
          ? "CSV downloaded for all active programs and sessions."
          : "CSV downloaded for all inactive programs and sessions.",
        severity: "success",
      });
    } catch (error: any) {
      console.error(error);
      setSnackbar({ open: true, message: error.response?.data?.message || "Failed to generate CSV for all programs.", severity: "error" });
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Manage Enrollments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage program enrollments
          </Typography>
        </Box>
      </Stack>

      {programs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={statusTab}
            onChange={handleStatusTabChange}
            aria-label="program status tabs"
          >
            <Tab label={`Active (${activePrograms.length})`} />
            <Tab label={`Inactive (${inactivePrograms.length})`} />
          </Tabs>
        </Box>
      )}

      {displayedPrograms.length === 0 && programs.length > 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {statusTab === 0 ? "No active programs" : "No inactive programs"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusTab === 0
              ? "Switch to the Inactive tab to view enrollments for deactivated programs."
              : "Switch to the Active tab to view enrollments for active programs."}
          </Typography>
        </Paper>
      ) : (
        <>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 300 }}>
                  <TextField
                    select
                    label="Select Program"
                    value={selectedProgramInTab ? selectedProgramId : ""}
                    onChange={(e) => setSelectedProgramId(e.target.value)}
                    sx={{ minWidth: 250 }}
                  >
                    {displayedPrograms.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.title}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Select Session"
                    value={selectedSessionId}
                    onChange={(e) => setSelectedSessionId(e.target.value)}
                    sx={{ minWidth: 250 }}
                    disabled={!sessions.length}
                  >
                    {sessions.map((s) => (
                      <MenuItem key={s.id || s.sessionId} value={s.id || s.sessionId}>
                        {s.name || "Untitled Session"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCurrentSessionCsv}
                    disabled={!selectedSessionId || downloadingCurrent || !enrollments.length}
                    sx={{ borderRadius: 2, px: 2 }}
                  >
                    {downloadingCurrent ? "Downloading..." : "Download CSV (Session)"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllCsv}
                    disabled={downloadingAll}
                    sx={{ borderRadius: 2, px: 2 }}
                  >
                    {downloadingAll ? "Downloading..." : "Download CSV (All Programs)"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={refreshing ? <RefreshIcon sx={{ animation: "spin 1s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={!selectedSessionId || refreshing}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </Button>
                  {canEdit && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<AddIcon />}
                      onClick={() => setAddDialogOpen(true)}
                      disabled={!selectedSessionId}
                      sx={{ borderRadius: 2, px: 3 }}
                    >
                      Add Enrollment
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {enrollments.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No enrollments yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedSessionId ? "Get started by adding your first enrollment" : "Please select a program and session first"}
              </Typography>
              {selectedSessionId && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add First Enrollment
                </Button>
              )}
            </Paper>
          ) : (
            <EnrollmentsDataGrid
              enrollments={enrollments}
              coaches={coaches}
              editingCoach={editingCoach}
              onCoachChange={onCoachChange}
              onCoachSave={onCoachSave}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onDelete={handleDeleteEnrollment}
              canEdit={canEdit}
            />
          )}
        </>
      )}

      {/* Dialogs */}
      {canEdit && (
        <>
          <AddEnrollmentDialog
            open={addDialogOpen}
            selectedSession={selectedSession}
            sessions={sessions}
            onClose={() => setAddDialogOpen(false)}
            onCreate={handleCreate}
          />

          <EditEnrollmentDialog
            open={editDialogOpen}
            enrollment={selectedEnrollment}
            onClose={() => setEditDialogOpen(false)}
            onUpdate={handleUpdateEnrollment}
          />
        </>
      )}

      <ViewEnrollmentDialog
        open={viewDialogOpen}
        enrollment={selectedEnrollment}
        coaches={coaches}
        onClose={() => setViewDialogOpen(false)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageProgramEnrollments;
